import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import SearchableSelect from 'components/SearchableSelect'
import { SkeletonLine } from 'components/Skeleton'
import Input from 'components/atoms/Input'
import {
  listDutyholderOrganisations,
  createDutyholderOrganisation
} from 'services/dutyholderOrganisationService'
import { attachRoleOrganisation } from 'services/BpService'
import { getApiErrors } from 'utils/errors'

const MAX_PAGES = 8

const emptyNewOrg = { name: '', contactName: '', contactEmail: '', contactPhone: '' }

const AssignOrganisationSection = ({ bpId, role, onDone }) => {
  const [orgs, setOrgs] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const [adding, setAdding] = useState(false)
  const [newOrg, setNewOrg] = useState(emptyNewOrg)
  const [busy, setBusy] = useState(false)

  const loadOrganisations = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    const collected = []
    try {
      let page = 1
      while (page <= MAX_PAGES) {
        const res = await listDutyholderOrganisations({ page })
        const incoming = res?.data?.data || []
        const meta = res?.data?.meta || {}
        incoming.forEach(o => {
          if (!collected.some(x => x.id === o.id)) collected.push(o)
        })
        const lastPage = meta.lastPage || page
        const currentPage = meta.currentPage || page
        if (incoming.length === 0 || currentPage >= lastPage) break
        page = currentPage + 1
      }
      setOrgs(collected)
    } catch (e) {
      setLoadError(true)
      const msg = getApiErrors(e)
      if (msg) toast.error(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadOrganisations()
  }, [loadOrganisations])

  const options = useMemo(
    () => orgs.map(o => ({ value: o.id, label: o.name, sublabel: o.contactName || undefined })),
    [orgs]
  )

  const assign = async organisationId => {
    if (!organisationId) return
    setBusy(true)
    try {
      await attachRoleOrganisation({ bpId, role, organisationId })
      toast.success('Organisation added to this role.')
      onDone?.()
    } catch (e) {
      const msg = getApiErrors(e)
      if (msg) toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  const handleNewOrgChange = (name, value) => setNewOrg(prev => ({ ...prev, [name]: value }))

  const resetNewOrgForm = () => {
    setAdding(false)
    setNewOrg(emptyNewOrg)
  }

  const createAndAssign = async () => {
    if (!newOrg.name.trim()) return
    setBusy(true)
    try {
      const res = await createDutyholderOrganisation({ ...newOrg, name: newOrg.name.trim() })
      const created = res?.data?.data
      if (!created?.id) throw new Error('Organisation was not returned by the server.')
      await assign(created.id)
    } catch (e) {
      const msg = getApiErrors(e)
      if (msg) toast.error(msg)
      setBusy(false)
    }
  }

  if (loading && orgs.length === 0) {
    return (
      <Wrap>
        <SkeletonLine $w='30%' $h={12} />
        <SkeletonLine $w='100%' $h={40} />
      </Wrap>
    )
  }

  return (
    <Wrap>
      {!adding ? (
        <>
          <IntroText>Choose an organisation to hold this role, or add a new one below.</IntroText>
          <FieldLabel>Organisation</FieldLabel>
          <SearchableSelect
            ariaLabel='Organisation'
            options={options}
            value={selectedId}
            onChange={setSelectedId}
            placeholder='Select an organisation…'
            searchPlaceholder='Search organisations…'
          />
          {loadError && orgs.length === 0 && (
            <ErrorHint>
              Could not load your organisations. <LinkBtn type='button' onClick={loadOrganisations}>Try again</LinkBtn>
            </ErrorHint>
          )}
          {!loadError && orgs.length === 0 && (
            <EmptyHint>You have not saved any organisations yet. Add one below.</EmptyHint>
          )}
          <PrimaryBtn type='button' disabled={!selectedId || busy} onClick={() => assign(selectedId)}>
            {busy ? 'Adding…' : 'Add this organisation'}
          </PrimaryBtn>
          <LinkBtn type='button' disabled={busy} onClick={() => setAdding(true)}>
            + Add a new organisation
          </LinkBtn>
        </>
      ) : (
        <>
          <IntroText>Add the organisation's details. Only the name is required.</IntroText>
          <AddForm>
            <Input
              required
              label='Organisation name'
              name='name'
              value={newOrg.name}
              onChange={handleNewOrgChange}
              nomarginbottom
            />
            <Input
              optional
              label='Contact name'
              name='contactName'
              value={newOrg.contactName}
              onChange={handleNewOrgChange}
              nomarginbottom
            />
            <Input
              optional
              type='email'
              label='Contact email'
              name='contactEmail'
              value={newOrg.contactEmail}
              onChange={handleNewOrgChange}
              nomarginbottom
            />
            <Input
              optional
              label='Contact phone'
              name='contactPhone'
              value={newOrg.contactPhone}
              onChange={handleNewOrgChange}
              nomarginbottom
            />
            <PrimaryBtn type='button' disabled={!newOrg.name.trim() || busy} onClick={createAndAssign}>
              {busy ? 'Saving…' : 'Save and add to role'}
            </PrimaryBtn>
            <LinkBtn type='button' disabled={busy} onClick={resetNewOrgForm}>
              Back to the list
            </LinkBtn>
          </AddForm>
        </>
      )}
    </Wrap>
  )
}

export default AssignOrganisationSection

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 4px 0 20px;
`

const IntroText = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.haiti};
`

const FieldLabel = styled.div`
  font-weight: bold;
  font-size: 12px;
  letter-spacing: 0.11px;
`

const EmptyHint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
`

const ErrorHint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.error};
`

const AddForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PrimaryBtn = styled.button`
  width: 100%;
  min-height: 44px;
  padding: 10px 16px;
  border: none;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.white};
  font: inherit;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: default; }
`

const LinkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  min-height: 40px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.secondary};
  font: inherit;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 0;

  &:disabled { opacity: 0.5; cursor: default; }
`
